import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { ReceiptOutlined } from '@mui/icons-material'
import React from 'react'
import ReactPaginate from 'react-paginate'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { getWorkRestrictionLog } from 'src/services/EpisodeOfCareService'
import { ISearchRequestBase } from 'src/viewModels/SearchRequestBase'
import { WorkRestrictionLogSummaryResult } from 'src/viewModels/WorkRestrictionLogSummaryResult'
import moment from 'moment'

interface IWorkRestrictionLogModalProps {
  isOpen: boolean
  closeModal: () => void
  episodeOfCareId: string
  patientHeader: string
}

interface IWorkRestrictionLogModalState {
  gridData: WorkRestrictionLogSummaryResult[]
  page: number
  pageCount: number
  firstRecordIndex: number
  lastRecordIndex: number
  totalItems: number
  pageSize: number
  isLoading: boolean
}

class WorkRestrictionLogModal extends React.Component<
  IWorkRestrictionLogModalProps,
  IWorkRestrictionLogModalState
> {
  constructor(props: IWorkRestrictionLogModalProps) {
    super(props)
    this.state = {
      gridData: [],
      page: 1,
      pageCount: 1,
      firstRecordIndex: 0,
      lastRecordIndex: 0,
      totalItems: 0,
      pageSize: 10,
      isLoading: false,
    }
  }

  private getWorkRestrictionLogData = async () => {
    const { page, pageSize } = this.state
    const data: ISearchRequestBase = {
      page,
      pageSize,
    }

    this.setState({ isLoading: true })

    getWorkRestrictionLog(this.props.episodeOfCareId, data)
      .then((response) => {
        this.setState(
          {
            gridData: response.items,
            pageCount: response.totalPages || 1,
            totalItems: response.totalItems || 0,
            isLoading: false,
          },
          () => {
            this.setPaginationOffsetData()
          }
        )
      })
      .catch(() => {
        this.setState({ isLoading: false })
      })
  }

  private setPaginationOffsetData = () => {
    const { page, totalItems, pageSize } = this.state
    const firstRecordIndex = (page - 1) * pageSize + 1
    const lastRecordIndex = page * pageSize < totalItems ? page * pageSize : totalItems
    this.setState({
      firstRecordIndex: firstRecordIndex,
      lastRecordIndex: lastRecordIndex,
    })
  }

  private handlePageChange = (event: any) => {
    const page = event.selected
    this.setState({ page: page + 1 }, () => this.getWorkRestrictionLogData())
  }

  private isGridDataEmpty = () => {
    return this.state.totalItems === 0
  }

  private formatDate = (dateString: string | null): string => {
    if (!dateString) return ''
    return moment(dateString).format('MM/DD/YYYY')
  }

  private mapGridData = () => {
    if (this.isGridDataEmpty()) {
      return (
        <TableRow key={1}>
          <TableCell colSpan={4} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    } else {
      return this.state.gridData.map((record, index) => {
        const completedBy = record.completedBy || ''

        return (
          <TableRow key={index}>
            <TableCell style={{ width: '29.42%' }} align="left">
              {record.taskName}
            </TableCell>
            <TableCell style={{ width: '11%' }} align="right">
              {this.formatDate(record.dateCompleted)}
            </TableCell>
            <TableCell style={{ width: '29.42%' }} align="center">
              {completedBy}
            </TableCell>
            <TableCell style={{ width: '11.25%' }} align="right">
              {this.formatDate(record.modifiedOn)}
            </TableCell>
          </TableRow>
        )
      })
    }
  }

  private paginationInfo = () => {
    if (!this.isGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {this.state.firstRecordIndex} to {this.state.lastRecordIndex} of{' '}
            {this.state.totalItems} entries
          </Typography>
        </div>
      )
    } else {
      return <></>
    }
  }

  public componentDidMount(): void {
    this.getWorkRestrictionLogData()
  }

  public render() {
    const { isOpen, closeModal, patientHeader } = this.props

    return (
      <Dialog
        key={this.props.episodeOfCareId}
        fullWidth={true}
        maxWidth="lg"
        open={isOpen}
        onClose={closeModal}
      >
        <DialogTitle style={{ padding: 0 }}>
          <ResponsiveAppBar
            title={`Work Restriction Log - ${patientHeader}`}
            pageIcon={<ReceiptOutlined />}
            isModal={true}
          />
        </DialogTitle>
        <DialogContent>
          {this.state.isLoading && (
            <div
              style={{
                position: 'relative',
                display: 'flex',
                width: '100%',
                height: '400px',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={60} />
            </div>
          )}
          {!this.state.isLoading && (
            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignContent="center"
              alignItems="flex-start"
              wrap="wrap"
            >
              <Grid item xs={12} style={{ minWidth: '750px' }}>
                <Table aria-label="work restriction log table">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: '29.42%' }} align="left">
                        Task Name
                      </TableCell>
                      <TableCell style={{ width: '11.25%' }} align="right">
                        Date Completed
                      </TableCell>
                      <TableCell style={{ width: '29.42%' }} align="center">
                        Completed By
                      </TableCell>
                      <TableCell style={{ width: '11.25%' }} align="right">
                        Date Modified
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody style={{ width: '100%' }}>{this.mapGridData()}</TableBody>
                </Table>
                <div className="pagination-row">
                  {this.paginationInfo()}
                  <ReactPaginate
                    previousLabel={'<'}
                    nextLabel={'>'}
                    onPageChange={this.handlePageChange}
                    forcePage={this.state.page - 1}
                    pageCount={this.state.pageCount}
                    containerClassName={'pagination'}
                    activeClassName={'active'}
                    //@ts-ignore
                    renderOnZeroPageCount={null}
                  />
                </div>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} data-cy="close">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
}

export default WorkRestrictionLogModal
